'use client';

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import s from './TagList.module.scss';
import classNames from 'classnames';

const TagList = ({ tags = [], size = '', before = null }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [firstOnLineIndices, setFirstOnLineIndices] = useState(new Set());
    const containerRef = useRef(null);

    const SHOWN_TAG_COUNT = 3;
    const WITH_GAP_COUNT = 7;

    const classes = classNames(
        s.Root,
        { [s['Root--Expanded']]: isExpanded },
        { [s['Root--Collapsed']]: tags.length > SHOWN_TAG_COUNT },
        { [s['Root--WithGap']]: true },
        { [s['Root--Small']]: size === 'small' }
    );

    const handleExpand = () => {
        if (tags.length > SHOWN_TAG_COUNT) {
            setIsExpanded(!isExpanded);
        }
    };

    useEffect(() => {
        const detectFirstOnLine = () => {
            if (!containerRef.current) return;

            const items =
                containerRef.current.querySelectorAll('.TagList_Item__');
            const newFirstOnLineIndices = new Set([0]);

            for (let i = 1; i < items.length; i++) {
                const currentRect = items[i].getBoundingClientRect();
                const prevRect = items[i - 1].getBoundingClientRect();

                if (currentRect.top > prevRect.bottom - 5) {
                    newFirstOnLineIndices.add(i);
                }
            }

            setFirstOnLineIndices(newFirstOnLineIndices);
        };

        const timer = setTimeout(detectFirstOnLine, 0);
        window.addEventListener('resize', detectFirstOnLine);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', detectFirstOnLine);
        };
    }, [tags, isExpanded]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target)
            ) {
                setIsExpanded(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const COLLAPSED_TAG_COUNT = SHOWN_TAG_COUNT + 2;
    const displayedTags = isExpanded
        ? tags
        : tags.slice(0, COLLAPSED_TAG_COUNT);
    const remainingCount = tags.length - COLLAPSED_TAG_COUNT;

    const renderItem = (tag, index) => (
        <div
            className={classNames(s.Item, 'TagList_Item__', {
                [s.First]: index === 0,
                [s.LastShown]:
                    index === SHOWN_TAG_COUNT - 1 &&
                    tags.length > SHOWN_TAG_COUNT,
                [s.IsAmongShown]: index < SHOWN_TAG_COUNT,
                [s.FirstOnLine]: firstOnLineIndices.has(index),
            })}
            key={index}
            style={{ zIndex: tags.length - index }}>
            <Tag
                label={tag.label}
                isActive={isExpanded || index === 0 || index < SHOWN_TAG_COUNT}
            />
        </div>
    );

    return (
        <div className={classes} onClick={handleExpand} ref={containerRef}>
            {/* {tags.length} {COLLAPSED_TAG_COUNT} {isExpanded.toString()} */}
            <div className={s.List}>
                {before}
                {displayedTags.slice(0, SHOWN_TAG_COUNT - 1).map(renderItem)}
                {displayedTags.length >= SHOWN_TAG_COUNT && (
                    <div className={s.Stack}>
                        {displayedTags
                            .slice(SHOWN_TAG_COUNT - 1)
                            .map((tag, index) =>
                                renderItem(tag, index + SHOWN_TAG_COUNT - 1)
                            )}
                        {tags.length > COLLAPSED_TAG_COUNT && !isExpanded && (
                            <div className={s.ExpandButton}>
                                +{remainingCount}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const Tag = ({ label, isActive }) => {
    const classes = classNames(s.Tag, { [s['Tag--Active']]: isActive });

    return (
        <div className={classes}>
            <span className={s.TagLabel}>{label}</span>
        </div>
    );
};

TagList.propTypes = {
    tags: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            isActive: PropTypes.bool,
        })
    ),
    before: PropTypes.node,
};

export default TagList;
